import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #2070 — batch-fetches every ScoreSheet across the running order's rides in
// ONE call (score-api's POST /scoresheet/batch, card #1961), grouped by
// rideId, so orderOfGo.js's deriveRideTermReason() call can resolve tier 4
// (a ScoreSheet's own eliminations[]) for a ride whose elimination hasn't
// been denormalized onto ride.terminationReason — the un-migrated #2056
// legacy shape. Mirrors scoreadmin-fe's ClassPlacings.jsx loadResults/
// applyResults grouping (card #2063 AC3), same client key (`scoreSheets`).
//
// Deliberately NOT polled on the same cadence as the status poll: a LIVE
// elimination submitted today is already stamped onto ride.terminationReason
// by score-api's scoresheet handler in the SAME request that saves it (tier
// 1 resolves it immediately, no sheets fetch needed) — this call only exists
// to catch the legacy gap, so a fetch whenever the running order's ride-id
// set changes, plus the same manual "refresh now" affordance every other
// data source here already has, is enough. Best-effort: a failed fetch
// degrades to no sheets (tiers 1-3 still resolve everything that isn't
// relying on tier 4) rather than blocking the running order.
export function useRideSheets(rideIds, { enabled = true } = {}) {
  const scoreApi = useScoreApi();
  const [sheetsByRideId, setSheetsByRideId] = useState(new Map());

  const ids = Array.from(new Set((rideIds ?? []).filter(Boolean)));
  const idsKey = ids.join(",");
  const idsRef = useRef(ids);
  idsRef.current = ids;

  const refresh = useCallback(async () => {
    const currentIds = idsRef.current;
    if (!currentIds.length) {
      setSheetsByRideId(new Map());
      return;
    }
    try {
      const sheets = await scoreApi.scoreSheets
        .listScoreSheetsForRides({ rideIds: currentIds })
        .then(unwrap);
      const next = new Map();
      for (const sheet of sheets ?? []) {
        if (!sheet?.rideId) continue;
        const list = next.get(sheet.rideId);
        if (list) list.push(sheet);
        else next.set(sheet.rideId, [sheet]);
      }
      setSheetsByRideId(next);
    } catch {
      // Degrade silently — see the doc comment above.
    }
  }, [scoreApi]);

  useEffect(() => {
    if (!enabled || !idsRef.current.length) return;
    refresh();
    // idsKey drives re-fetch when the running order's ride-id set changes;
    // refresh is stable across renders (only scoreApi in its deps).
  }, [idsKey, enabled, refresh]);

  return { sheetsByRideId, refresh };
}

export default useRideSheets;
